/**
 * src/pages/auth/ForgotPasswordPage.tsx
 * Password recovery by one-time code.
 * 1. Operator enters their account email and chooses email or SMS delivery.
 * 2. They enter the 6-digit code.
 * 3. On success they are sent to /reset-password with a short-lived reset token.
 *
 * The server answers identically whether or not the account exists (and whether or
 * not it has a phone number), so this page can never confirm an account - the copy
 * says "if an account matches".
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestResetCode, verifyResetCode, type RecoveryChannel } from '@/lib/services/recovery';
import { ApiError } from '@/lib/api/client';
import { CodeEntry } from '@/components/auth/CodeEntry';
import { codeLength } from '@/lib/utils/codeLength';
import { GlowLogo } from '@/components/ui/GlowLogo';

const CHANNEL_LABEL: Record<RecoveryChannel, string> = { email: 'Email', sms: 'Text message (SMS)' };

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'request' | 'code'>('request');
  const [email, setEmail] = useState('');
  const [channel, setChannel] = useState<RecoveryChannel>('email');
  const [cooldown, setCooldown] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(ch: RecoveryChannel): Promise<number> {
    const r = await requestResetCode(email.trim(), ch);
    setChannel(ch);
    setCooldown(r.resend_after_seconds);
    return r.resend_after_seconds;
  }

  async function handleSendCode() {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await send(channel);
      setStep('code');
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(code: string) {
    const { reset_token } = await verifyResetCode(email.trim(), channel, code);
    // Router state, not the URL: the token stays out of history, logs and referrers.
    navigate('/reset-password', { state: { token: reset_token }, replace: true });
  }

  const other: RecoveryChannel = channel === 'email' ? 'sms' : 'email';

  return (
    <main className="ambient-bg min-h-screen flex items-center justify-center px-6">
      <div className="screen-fade" style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 2 }}>
        <header className="mb-8 text-center">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GlowLogo size="lg" />
          </div>
          <p className="mono-label" style={{ marginTop: 12, color: 'rgba(var(--fg-rgb),0.45)', letterSpacing: '0.1em' }}>
            ACCOUNT RECOVERY
          </p>
        </header>

        <div className="text-center" style={{ marginBottom: 28 }}>
          <h2
            className="font-serif"
            style={{ fontSize: 28, fontWeight: 400, color: 'var(--color-on-surface)', margin: '0 0 12px' }}
          >
            Verify Your Identity
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-manrope)',
              fontSize: 13,
              color: 'var(--color-on-surface-variant)',
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            {step === 'request'
              ? 'Enter your account email and choose how to receive a one-time code.'
              : 'Enter the code to continue.'}
          </p>
        </div>

        {step === 'request' ? (
          <>
            <label className="block" style={{ marginBottom: 20 }}>
              <span className="mono-label" style={{ display: 'block', marginBottom: 6 }}>
                Email address
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="underline-input"
                autoComplete="email"
                placeholder="you@glowhire.co.za"
                disabled={loading}
              />
            </label>

            <fieldset style={{ border: 'none', padding: 0, margin: '0 0 24px' }}>
              <legend className="mono-label" style={{ marginBottom: 8 }}>
                Send my code by
              </legend>
              {(Object.keys(CHANNEL_LABEL) as RecoveryChannel[]).map((ch) => (
                <label
                  key={ch}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    name="channel"
                    checked={channel === ch}
                    onChange={() => setChannel(ch)}
                    disabled={loading}
                  />
                  <span style={{ fontFamily: 'var(--font-manrope)', fontSize: 13, color: 'var(--color-on-surface)' }}>
                    {CHANNEL_LABEL[ch]}
                  </span>
                </label>
              ))}
              {channel === 'sms' && (
                <p style={{ fontSize: 12, color: 'rgba(var(--fg-rgb),0.45)', margin: '6px 0 0' }}>
                  We text the mobile number saved on your account.
                </p>
              )}
            </fieldset>

            <button
              type="button"
              onClick={handleSendCode}
              disabled={loading}
              className="btn-pill"
              style={{ width: '100%', opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Sending' : 'Send Code'}
            </button>

            {error && (
              <p
                style={{
                  fontFamily: 'var(--font-manrope)',
                  fontSize: 13,
                  color: 'var(--color-error)',
                  textAlign: 'center',
                  marginTop: 10,
                }}
              >
                {error}
              </p>
            )}
          </>
        ) : (
          <CodeEntry
            message={`If an account matches ${email.trim()}, we sent a ${codeLength(channel)}-digit code ${
              channel === 'sms' ? 'by SMS to the mobile number on file' : 'to that email address'
            }. It expires in 5 minutes.`}
            initialCooldown={cooldown}
            length={codeLength(channel)}
            onVerify={handleVerify}
            onResend={() => send(channel)}
            verifyLabel="Verify Code"
            alt={{ label: `Use ${other === 'sms' ? 'SMS' : 'email'} instead`, run: () => send(other) }}
          />
        )}

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <a
            href="/login"
            className="mono-label"
            style={{
              color: 'rgba(var(--fg-rgb),0.3)',
              textDecoration: 'none',
              letterSpacing: '0.08em',
              fontSize: 10,
              textTransform: 'uppercase',
            }}
          >
            Return to login
          </a>
        </div>
      </div>
    </main>
  );
}
